// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * A selected room's grips on the plan (frontend-u0o.6, slice S6): a
 * circle on each corner (drag it; double-click takes it out) and a bar on
 * each edge's middle (drag it to move the edge square to itself, Revit's
 * "edit boundary"; double-click puts a corner there). Holes have their own.
 * Every change is one `space.update` of `polygon` — the server measures
 * area, perimeter and volume again — refused here when the outline would
 * cross itself or shrink below the smallest room. Pure: no React, no DOM.
 */

import type { ModelEditOp, PlanXY, SolidSpace } from '../api/types';
import { spaceLabel } from './geometri';
import type { XY } from './planWalls';
import { MIN_ROOM_AREA, ringProblem } from './roomTool';
import { ringArea } from './spaces';

/** Which ring: -1 the outer, else the hole's index. */
export type RingRef = number;

export interface RoomGrip {
  kind: 'vertex' | 'edge';
  ring: RingRef;
  /** The corner's index, or the edge's (from corner `index` to `index + 1`). */
  index: number;
  at: XY;
  /** An edge's outward-ish unit normal (left of its direction), for the drag. */
  normal?: XY;
}

const ringOf = (s: Pick<SolidSpace, 'polygon'>, ring: RingRef): readonly PlanXY[] => (ring < 0 ? s.polygon.outer : (s.polygon.holes[ring] ?? []));

/** The grips of a room: every corner, every edge's middle (edges shorter than `minEdge` metres get none). */
export function roomGrips(s: Pick<SolidSpace, 'polygon'>, minEdge = 0.2): RoomGrip[] {
  const out: RoomGrip[] = [];
  const rings: RingRef[] = [-1, ...s.polygon.holes.map((_, i) => i)];
  for (const r of rings) {
    const pts = ringOf(s, r);
    const n = pts.length;
    if (n < 3) continue;
    pts.forEach((p, i) => out.push({ kind: 'vertex', ring: r, index: i, at: [p[0], p[1]] }));
    for (let i = 0; i < n; i += 1) {
      const a = pts[i];
      const b = pts[(i + 1) % n];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < minEdge) continue;
      out.push({ kind: 'edge', ring: r, index: i, at: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], normal: [-(b[1] - a[1]) / L, (b[0] - a[0]) / L] });
    }
  }
  return out;
}

const r6 = (v: number) => Math.round(v * 1e6) / 1e6;
const pt = (p: XY): PlanXY => [r6(p[0]), r6(p[1])];

function withRing(s: Pick<SolidSpace, 'polygon'>, ring: RingRef, next: XY[]): SolidSpace['polygon'] {
  const ptsOut = next.map(pt);
  if (ring < 0) return { outer: ptsOut, holes: s.polygon.holes.map((h) => h.map((p) => [p[0], p[1]] as PlanXY)) };
  return { outer: s.polygon.outer.map((p) => [p[0], p[1]] as PlanXY), holes: s.polygon.holes.map((h, i) => (i === ring ? ptsOut : h.map((p) => [p[0], p[1]] as PlanXY))) };
}

export type PolygonEdit = { polygon: SolidSpace['polygon'] } | { error: string };

/** Corner `index` of `ring` moved to `to`. */
export function moveVertex(s: Pick<SolidSpace, 'polygon'>, ring: RingRef, index: number, to: XY): PolygonEdit {
  const pts = ringOf(s, ring).map((p) => [p[0], p[1]] as XY);
  if (!pts[index]) return { error: 'Hjørnet findes ikke' };
  pts[index] = to;
  return check(s, ring, pts);
}

/** Edge `index` of `ring` moved `offset` metres along its normal (both its corners; the neighbours stretch). */
export function moveEdge(s: Pick<SolidSpace, 'polygon'>, ring: RingRef, index: number, offset: number): PolygonEdit {
  const pts = ringOf(s, ring).map((p) => [p[0], p[1]] as XY);
  const n = pts.length;
  const a = pts[index];
  const b = pts[(index + 1) % n];
  if (!a || !b) return { error: 'Kanten findes ikke' };
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(L > 1e-9)) return { error: 'Kanten har ingen længde' };
  const nrm: XY = [(-(b[1] - a[1]) / L) * offset, ((b[0] - a[0]) / L) * offset];
  // Each corner slides along its other edge so the neighbours keep their direction (Revit's boundary edit).
  const slide = (corner: number, other: number): XY => {
    const c = pts[corner];
    const o = pts[other];
    const d: XY = [c[0] - o[0], c[1] - o[1]];
    const along = d[0] * nrm[0] + d[1] * nrm[1];
    const nn = nrm[0] * nrm[0] + nrm[1] * nrm[1];
    // A neighbour edge square to the moved one carries the corner straight; a slanted one stretches along itself.
    if (Math.abs(along) < 1e-9 || nn === 0) return [c[0] + nrm[0], c[1] + nrm[1]];
    const k = nn / along;
    return [c[0] + d[0] * k, c[1] + d[1] * k];
  };
  const ia = index;
  const ib = (index + 1) % n;
  const na = slide(ia, (ia + n - 1) % n);
  const nb = slide(ib, (ib + 1) % n);
  pts[ia] = na;
  pts[ib] = nb;
  return check(s, ring, pts);
}

/** A corner put on edge `index` at `at` (its middle by default). */
export function insertVertex(s: Pick<SolidSpace, 'polygon'>, ring: RingRef, index: number, at?: XY): PolygonEdit {
  const pts = ringOf(s, ring).map((p) => [p[0], p[1]] as XY);
  const n = pts.length;
  const a = pts[index];
  const b = pts[(index + 1) % n];
  if (!a || !b) return { error: 'Kanten findes ikke' };
  pts.splice(index + 1, 0, at ?? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
  return check(s, ring, pts);
}

/** Corner `index` taken out (a room keeps three). */
export function removeVertex(s: Pick<SolidSpace, 'polygon'>, ring: RingRef, index: number): PolygonEdit {
  const pts = ringOf(s, ring).map((p) => [p[0], p[1]] as XY);
  if (pts.length <= 3) return { error: 'Et rum skal have mindst tre hjørner' };
  pts.splice(index, 1);
  return check(s, ring, pts);
}

function check(s: Pick<SolidSpace, 'polygon'>, ring: RingRef, pts: XY[]): PolygonEdit {
  const problem = ringProblem(pts);
  if (problem) return { error: ring < 0 ? problem : `Hullet: ${problem.charAt(0).toLowerCase()}${problem.slice(1)}` };
  const poly = withRing(s, ring, pts);
  const area = Math.abs(ringArea(poly.outer)) - poly.holes.reduce((a, h) => a + Math.abs(ringArea(h)), 0);
  if (area < MIN_ROOM_AREA) return { error: 'Rummet bliver for lille' };
  return { polygon: poly };
}

/** The `space.update` of an edit (null when it changes nothing). */
export function polygonOps(s: SolidSpace, edit: PolygonEdit): ModelEditOp[] | null {
  if ('error' in edit) return null;
  const same =
    edit.polygon.outer.length === s.polygon.outer.length &&
    edit.polygon.outer.every((p, i) => Math.abs(p[0] - s.polygon.outer[i][0]) < 5e-4 && Math.abs(p[1] - s.polygon.outer[i][1]) < 5e-4) &&
    edit.polygon.holes.length === s.polygon.holes.length &&
    edit.polygon.holes.every((h, j) => h.length === s.polygon.holes[j].length && h.every((p, i) => Math.abs(p[0] - s.polygon.holes[j][i][0]) < 5e-4 && Math.abs(p[1] - s.polygon.holes[j][i][1]) < 5e-4));
  return same ? null : [{ op: 'space.update', guid: s.guid, set: { polygon: edit.polygon } }];
}

/** The history label of a grip edit: "Flyt hjørne · Køkken". */
export function gripLabel(kind: 'vertex' | 'edge' | 'insert' | 'remove', s: Pick<SolidSpace, 'id' | 'name'>): string {
  const what = kind === 'vertex' ? 'Flyt hjørne' : kind === 'edge' ? 'Flyt kant' : kind === 'insert' ? 'Nyt hjørne' : 'Fjern hjørne';
  return `${what} · ${spaceLabel(s)}`;
}
